import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAnchoredList } from '../../components/ui/useAnchoredList';
import { ScanIcon } from '../../components/ui/icons';
import * as scanner from '../../platform/scanner';
import { ScanSheet } from '../scan/ScanSheet';
import { resolveScan } from '../scan/api';
import { getComponent, searchComponents, type ComponentBrief } from './api';

interface Props {
  label: string;
  value: ComponentBrief | null;
  onChange: (component: ComponentBrief | null) => void;
  /** A part to keep out of the results — normally the one being edited, which
   *  cannot be its own substitute or its own alternative. Filtered here so the
   *  offer never appears, rather than refused after it is taken. */
  exclude?: string;
}

/**
 * Picks one part out of the catalogue by the same search the catalogue screen
 * uses — part number, name or value — so what someone remembers about a part is
 * enough to order it.
 *
 * The result list is positioned against the input rather than nested inside it:
 * every pane in this application scrolls, and an ordinary dropdown gets cut off
 * by the first one of those it meets. See `useAnchoredList`.
 */
export function ComponentPicker({ label, value, onChange, exclude }: Props) {
  const [query, setQuery] = useState('');
  const [camera, setCamera] = useState(false);
  const [focused, setFocused] = useState(false);
  const anchor = useRef<HTMLInputElement>(null);

  const results = useQuery({
    queryKey: ['components', 'search', { q: query }],
    queryFn: () => searchComponents({ q: query }),
    enabled: query.length > 0,
  });

  const hits = (results.data ?? []).filter((hit) => hit.component.id !== exclude);
  const open = focused && Boolean(query) && !value && hits.length > 0;
  const position = useAnchoredList(anchor, open);
  const cameraReady = scanner.cameraSupport() === 'ok';
  const acceptScan = async (code: string) => {
    const found = await resolveScan(code);
    if (!found.resolved || found.match?.kind !== 'component') return;
    const component = await getComponent(found.match.id);
    onChange(component);
    setQuery(component.name);
  };

  return (
    <span
      className="stock__picker"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      <span className="stock__picker-field"><input
        ref={anchor}
        value={value ? value.name : query}
        onChange={(event) => {
          setQuery(event.target.value);
          onChange(null);
        }}
        onFocus={() => setFocused(true)}
        placeholder={label}
      />
      {cameraReady && <button type="button" className="stock__picker-scan" aria-label={label} onClick={() => setCamera(true)}><ScanIcon /></button>}
      </span>
      {camera && <ScanSheet title={label} onRead={(code) => { setCamera(false); void acceptScan(code); }} onCancel={() => setCamera(false)} />}
      {open && (
        <ul className="stock__picker-results" style={position}>
          {hits.map(({ component }) => (
            <li key={component.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(component);
                  setQuery(component.name);
                }}
              >
                {component.name}
                <em> {component.mpn ?? component.internal_code}</em>
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}
